/**
 * UX stage 3.4 — the client's answer to a finished fixed-price service.
 *
 * Binding (docs/analysis/RM24-UX-otsused.md):
 *   - B2: the worker's „Töö on tehtud” is a completion NOTICE, not acceptance.
 *   - D6: the service is accepted by the person entitled to it (the object's
 *     responsible — `approve_hours`, the same right as hours); a partial
 *     acceptance / a claim is decided by RM24 and the disputed part is NOT
 *     accepted automatically.
 *   - The answer is INFORMATION ONLY, with no monetary effect (Marti 2026-11-03):
 *     a claim lets RM24 know and is handled in the existing invoice review /
 *     credit note process.
 *
 * Written only by `respondServiceCompletion` (Admin SDK) on the job document,
 * which every company member may read; clients cannot write jobs (rules).
 *
 * The claim's TEXT is not on the job: assigned workers read the job document.
 * It is `detail.claimText` on the audit row `SERVICE_ANSWER_AUDIT_ID` (the
 * rules give the client and staff that row, not workers). A fixed id because a
 * client reads it with a get: a list query on auditLog is refused by the rule.
 */

import type { Job } from './job';

export type ServiceAcceptanceStatus = 'accepted' | 'claimed';

export interface ServiceAcceptance {
  status: ServiceAcceptanceStatus;
  /** uid of the client user who answered. */
  by: string;
  /** ISO instant of the answer. */
  at: string;
}

/** `jobs/{jobId}/auditLog/{this}` — the one answer an order can have. */
export const SERVICE_ANSWER_AUDIT_ID = 'service_answer';

/** Statuses in which a fixed-price service can be answered (work started or done). */
export const SERVICE_RESPONSE_JOB_STATUSES = ['in_progress', 'completed', 'invoiced'] as const;

/** Whether the client may answer this order's service completion at all. */
export function serviceResponseOpen(
  job: Pick<Job, 'status'> & { requirements?: { pricingType?: string } | null },
): boolean {
  return (
    job.requirements?.pricingType === 'fixed' &&
    (SERVICE_RESPONSE_JOB_STATUSES as readonly string[]).includes(job.status)
  );
}
